import { Component, Element, h, Host, Prop } from '@stencil/core';

import { ClassNames } from '../../utils/interfaces';

export type Target = 'blank' | 'parent' | 'self' | 'top';
export type TargetPrefixed = '_blank' | '_parent' | '_self' | '_top';

@Component({
  tag: 'patternlib-textlink',
  styleUrl: 'patternlib-textlink.scss',
  shadow: true,
})
export class PatternlibTextlink {
  private hasIconSlot: boolean;
  private hasLabel: boolean;

  @Element() hostElement: HTMLPatternlibTextlinkElement;

  /**
   * Set the Textlink label.
   */
  @Prop() label = '';

  /**
   * Define the Textlink width.
   */
  @Prop() width = 'auto';

  /**
   * Set a hyperlink.
   */
  @Prop() href = '';

  /**
   * Define the Textlink size.
   */
  @Prop() size: 'big' | 'small' = 'big';

  /**
   * Define the target of the hyperlink. If no underscore prefix is provided it will be added automatically.
   */
  @Prop() target: Target | TargetPrefixed = 'blank';

  /**
   * Set the variant of the Textlink.
   */
  @Prop() variant: 'dark' | 'light' = 'light';
  /**
   * Set the icon position.
   */
  @Prop() iconPosition: 'left' | 'right' = 'left';

  /**
   * Define if the text is to be rendered uppercase.
   */
  @Prop() uppercase = true;

  /**
   * Define if the text is to be rendered bold.
   */
  @Prop() bold = true;

  /**
   * Disable the Textlink.
   */
  @Prop() disabled = false;

  componentWillLoad(): void {
    this.hasIconSlot = this.hostElement.querySelector('[slot="icon"]') !== null;
    this.hasLabel = this.label != undefined && this.label != '';
  }

  componentWillUpdate(): void {
    this.hasLabel = this.label != undefined && this.label != '';
  }

  private getFormattedTarget(target: Target | TargetPrefixed): TargetPrefixed {
    const targetPrefix = target[0];
    if (targetPrefix !== '_') {
      return `_${target}` as TargetPrefixed;
    }
    return target as TargetPrefixed;
  }

  render() {
    const textLinkClass: ClassNames = {
      textlink: true,
      [this.size]: true,
      [this.variant]: true,
      [this.hasIconSlot ? 'withicon' : 'textonly']: true,
      uppercase: this.uppercase,
      bold: this.bold,
      disabled: this.disabled,
    };

    const layoutClass: ClassNames = {
      [`textlink-content-wrapper-icon-and-label`]: this.label != undefined && this.hasIconSlot,
      [`textlink-content-wrapper-icon`]: this.label === undefined && this.hasIconSlot,
      [`textlink-content-wrapper-label`]: this.label != undefined && !this.hasIconSlot,
    };

    const iconClass: ClassNames = {
      icon: true,
      [`textlink-icon-padding-right`]: this.iconPosition === 'left' && this.hasLabel && this.hasIconSlot,
      [`textlink-icon-padding-left`]: this.iconPosition === 'right' && this.hasLabel && this.hasIconSlot,
    };

    const icon = (
      <div class={iconClass}>
        <slot name="icon" />
      </div>
    );

    const layout = (
      <a href={this.href != '' ? `${this.href}` : undefined} target={this.getFormattedTarget(this.target)}>
        <div class={layoutClass}>
          {this.hasIconSlot && this.iconPosition === 'left' ? icon : ''}
          {this.label}
          {this.hasIconSlot && this.iconPosition === 'right' ? icon : ''}
        </div>
      </a>
    );

    return (
      <Host>
        <span class={textLinkClass} style={{ width: `${this.width}` }}>
          {layout}
        </span>
      </Host>
    );
  }
}
